/**
 * Shared push-to-talk STT hook (Deepgram). Used by the marketing Copilot and the Help & feedback panel.
 *
 * Provides push-to-talk microphone input via Deepgram STT.
 * Transcribed text is returned via onTranscript callback — the caller
 * sends it to the chat as if the user typed it.
 *
 * TTS output is handled separately by the caller.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { resolveSttLanguage } from '@/utils/sttLanguageResolver'

export interface UsePushToTalkSTTOptions {
  /** Called with the final transcribed text */
  onTranscript: (text: string) => void
  /** Language hint for STT: 'en', 'es', or 'multi' */
  language?: string
  /** How long a pause ends the utterance and closes the mic, in ms (default 1000). */
  utteranceEndMs?: number
}

export interface UsePushToTalkSTTReturn {
  /** Whether the mic is currently active */
  isListening: boolean
  /** Partial (live) transcript during recording */
  partialTranscript: string
  /** Audio level 0-1 for visualization */
  audioLevel: number
  /** Start recording */
  startListening: () => Promise<void>
  /** Stop recording and finalize */
  stopListening: () => void
  /** Whether voice is available (credentials configured) */
  isAvailable: boolean
  /** Error if any */
  error: string | null
}

export function usePushToTalkSTT({
  onTranscript,
  language = 'multi',
  utteranceEndMs = 1000,
}: UsePushToTalkSTTOptions): UsePushToTalkSTTReturn {
  const [isListening, setIsListening] = useState(false)
  const [partialTranscript, setPartialTranscript] = useState('')
  const [audioLevel, setAudioLevel] = useState(0)
  const [isAvailable, setIsAvailable] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  // Committed (is_final) transcript segments. Deepgram interim results each
  // contain the WHOLE in-progress segment (a refinement, not a delta), so they
  // must REPLACE the live tail — not append — or the text duplicates.
  const finalTranscriptRef = useRef('')
  // Always points at the latest stopListening so the WS onmessage handler (set
  // up before stopListening is defined) can auto-close on end-of-speech.
  const stopRef = useRef<() => void>(() => {})

  const startListening = useCallback(async () => {
    setError(null)
    setPartialTranscript('')
    finalTranscriptRef.current = ''

    try {
      // Get Deepgram token from backend. The route is POST-only (a bare GET
      // 405s); mirror the persona-voice path (useVoiceConversation / AvatarRAGEngine).
      const tokenRes = await apiClient.fetch('/api/voice/deepgram-token', {
        method: 'POST',
        body: JSON.stringify({ ttl_seconds: 30 }),
      })
      if (!tokenRes.ok) {
        setIsAvailable(false)
        setError('Voice not configured — add Deepgram credentials in Settings')
        return
      }
      const { token } = await tokenRes.json()

      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      mediaStreamRef.current = stream

      // Map language to Deepgram format via cascade resolver
      const dgLang = resolveSttLanguage({ userSttLanguage: language })

      // Connect to Deepgram WebSocket. Authenticate via the WS sub-protocol.
      // /api/voice/deepgram-token returns an ephemeral JWT from /v1/auth/grant,
      // which requires the 'Bearer' scheme ('token' is for raw API keys) —
      // matching DeepgramSTTProvider. Using 'token' here fails the handshake.
      // endpointing + utterance_end_ms let Deepgram detect end-of-speech so the
      // mic auto-closes after a ~1s pause (no second click). Mirrors
      // DeepgramSTTProvider; UtteranceEnd needs interim_results=true.
      const ws = new WebSocket(
        `wss://api.deepgram.com/v1/listen?encoding=linear16&sample_rate=16000&channels=1&model=nova-3&language=${dgLang}&punctuate=true&interim_results=true&endpointing=300&utterance_end_ms=${utteranceEndMs}&vad_events=true`,
        ['Bearer', token],
      )

      wsRef.current = ws

      ws.onopen = () => {
        setIsListening(true)

        // Set up audio processing
        const audioContext = new AudioContext({ sampleRate: 16000 })
        audioContextRef.current = audioContext
        const source = audioContext.createMediaStreamSource(stream)
        const processor = audioContext.createScriptProcessor(4096, 1, 1)
        processorRef.current = processor

        processor.onaudioprocess = (e) => {
          const inputData = e.inputBuffer.getChannelData(0)

          // Calculate audio level for visualization
          let sum = 0
          for (let i = 0; i < inputData.length; i++) {
            sum += inputData[i] * inputData[i]
          }
          const rms = Math.sqrt(sum / inputData.length)
          setAudioLevel(Math.min(rms * 5, 1))

          // Convert Float32 to Int16 PCM
          const pcm = new Int16Array(inputData.length)
          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]))
            pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff
          }

          if (ws.readyState === WebSocket.OPEN) {
            ws.send(pcm.buffer)
          }
        }

        source.connect(processor)
        processor.connect(audioContext.destination)
      }

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data)

        // End-of-speech: Deepgram saw ~1s of silence after the last word.
        // Auto-close the mic (finalizes + delivers the transcript).
        if (data.type === 'UtteranceEnd') {
          stopRef.current()
          return
        }

        const transcript = data.channel?.alternatives?.[0]?.transcript
        if (!transcript) return

        const committed = finalTranscriptRef.current
        if (data.is_final) {
          // Commit this finalized segment, then display the committed text.
          finalTranscriptRef.current = committed ? `${committed} ${transcript}` : transcript
          setPartialTranscript(finalTranscriptRef.current)
        } else {
          // Interim: show committed segments + the live (replaceable) tail.
          setPartialTranscript(committed ? `${committed} ${transcript}` : transcript)
        }
      }

      ws.onerror = () => {
        setError('Voice connection error')
        cleanup()
      }

      ws.onclose = () => {
        // When WS closes, finalize the transcript
        setIsListening(false)
        setAudioLevel(0)
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Microphone access denied')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to start voice')
      }
      setIsListening(false)
    }
  }, [language, utteranceEndMs])

  const cleanup = useCallback(() => {
    // Stop audio processing
    if (processorRef.current) {
      processorRef.current.disconnect()
      processorRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    setAudioLevel(0)
  }, [])

  const stopListening = useCallback(() => {
    // Close WebSocket (triggers onclose which sets isListening=false)
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close()
    }
    wsRef.current = null
    cleanup()

    // Send the accumulated transcript (committed finals + any live tail).
    setPartialTranscript((transcript) => {
      if (transcript.trim()) {
        // Use setTimeout to avoid calling during render
        setTimeout(() => onTranscript(transcript.trim()), 0)
      }
      return ''
    })
    finalTranscriptRef.current = ''

    setIsListening(false)
  }, [cleanup, onTranscript])
  // Keep the ref fresh so the WS onmessage closure auto-closes via the latest stopListening.
  stopRef.current = stopListening

  // Cleanup all resources on unmount to prevent mic/WebSocket leaks
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close()
        }
        wsRef.current = null
      }
      if (processorRef.current) {
        processorRef.current.disconnect()
        processorRef.current = null
      }
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop())
        mediaStreamRef.current = null
      }
    }
  }, [])

  return {
    isListening,
    partialTranscript,
    audioLevel,
    startListening,
    stopListening,
    isAvailable,
    error,
  }
}
